<template>
    <!-- Freshchat injects its widget into the document via external script -->
    <span hidden aria-hidden="true" />
</template>

<script>
import { createNamespacedHelpers } from 'vuex';

import { initialState } from '@/Helpers.js';

const { mapState } = createNamespacedHelpers('Auth');

export const FRESHCHAT_LAUNCHER_SELECTORS = '[data-testid="launcher"], .fc-launcher, [class*="launcher"]';

export const HELP_SUPPORT_BODY_CLASS = 'help-support-page';

/** Stable DOM id so we can find an already-injected Freshchat script regardless of cache-bust query params. */
export const FRESHCHAT_SCRIPT_ID = 'freshchat-js-sdk';

export default {
    name: 'ChatbotInitializer',

    data: () => ({
        isInitialized: false,
        scriptLoaded: false,
    }),

    computed: {
        ...mapState(['user']),
        chatbotConfig() {
            return {
                enabled: initialState('ChatbotConfig.enabled', true),
                widgetId: initialState('ChatbotConfig.widgetId', ''),
                scriptUrl: initialState('ChatbotConfig.scriptUrl', ''),
                roleMapping: initialState('ChatbotConfig.roleMapping', {}),
                primaryGroupCategories: initialState('ChatbotConfig.primaryGroupCategories', {}),
                widgetConfig: initialState('ChatbotConfig.widgetConfig', {}),
            };
        },
        shouldHideLauncher() {
            return this.chatbotConfig.widgetConfig.hide_launcher ?? true;
        },
        userRole() {
            if (!this.user) return null;
            const userRoles = this.user.roles || [];
            const mappedRole = userRoles
                .map(role => this.chatbotConfig.roleMapping[role.name])
                .find(mapping => mapping);
            return mappedRole || 'edverum_student';
        },
        primaryGroup() {
            if (!this.user || !this.user.userGroups) return null;
            const institutionName = initialState('InstitutionSettings.name', '').toLowerCase();
            const categoryName =
                this.chatbotConfig.primaryGroupCategories[institutionName] ||
                this.chatbotConfig.primaryGroupCategories.default ||
                'Cohort';
            const primaryGroup = this.user.userGroups.find(group => group.group?.category?.name === categoryName);
            return primaryGroup?.group?.name || null;
        },
        shouldInitialize() {
            return (
                this.user &&
                this.chatbotConfig.enabled &&
                !this.isInitialized &&
                !this.$store.getters['User/hasActiveAttempt'] &&
                this.isHelpPage
            );
        },
        isHelpPage() {
            return this.$route.path === '/help' || this.$route.path.startsWith('/help/');
        },
    },

    watch: {
        user: {
            handler() {
                if (this.shouldInitialize) this.initializeChatbot();
            },
            immediate: true,
        },
        '$store.getters.User/hasActiveAttempt': {
            handler(hasActiveAttempt) {
                if (hasActiveAttempt && this.isInitialized) {
                    this.destroyChatbot();
                } else if (!hasActiveAttempt && this.shouldInitialize) {
                    this.initializeChatbot();
                }
            },
            immediate: true,
        },
        $route: {
            handler() {
                if (this.isHelpPage) {
                    this.setHelpPageActive(true);

                    if (this.shouldInitialize) {
                        this.initializeChatbot();
                    } else if (this.isInitialized) {
                        this.hideLauncher();
                    }
                } else if (this.isInitialized) {
                    this.setHelpPageActive(false);
                    this.hideWidgetOffPage();
                } else {
                    this.setHelpPageActive(false);
                }
            },
            immediate: true,
        },
    },

    beforeUnmount() {
        this.destroyChatbot();
    },

    methods: {
        buildMessengerConfig() {
            const headerProperty = {
                appName: this.chatbotConfig.widgetConfig.app_name ?? 'Edverum Support',
            };

            if (this.shouldHideLauncher) {
                headerProperty.hideChatButton = true;
            }

            return {
                config: {
                    eagerLoad: this.chatbotConfig.widgetConfig.eager_load ?? true,
                    fullscreen: this.chatbotConfig.widgetConfig.fullscreen ?? false,
                    headerProperty,
                },
                tags: [this.userRole],
                externalId: this.user.id.toString(),
            };
        },

        setHelpPageActive(active) {
            document.body.classList.toggle(HELP_SUPPORT_BODY_CLASS, active);
        },

        async initializeChatbot() {
            if (!this.shouldInitialize) return;
            if (!this.chatbotConfig.widgetId || !this.chatbotConfig.scriptUrl) {
                console.warn('Chatbot: Widget ID or Script URL missing.');
                return;
            }

            window.fcWidgetMessengerConfig = this.buildMessengerConfig();
            window.fcSettings = {
                onInit: () => {
                    if (!window.fcWidget) return;
                    window.fcWidget.setExternalId(this.user.id.toString());
                    window.fcWidget.user.setFirstName(this.user.first_name);
                    window.fcWidget.user.setLastName(this.user.last_name);
                    window.fcWidget.user.setEmail(this.user.email);
                    window.fcWidget.user.setProperties({
                        cf_external_id: this.user.external_id || '',
                        cf_primary_group: this.primaryGroup || '',
                        cf_user_role: this.userRole,
                        cf_institution: initialState('InstitutionSettings.name', ''),
                        cf_user_id: this.user.id.toString(),
                    });
                    this.hideLauncher();
                },
            };
            if (!this.scriptLoaded && !document.getElementById(FRESHCHAT_SCRIPT_ID)) {
                await this.loadFreshchatScript();
            }
            this.isInitialized = true;
            this.setHelpPageActive(true);
            window.edverumChatbot = {
                open: () => this.openChatbot(),
                close: () => window.fcWidget && window.fcWidget.close(),
                isOpen: () => window.fcWidget && window.fcWidget.isOpen(),
                show: () => this.showChatbot(),
                hide: () => this.hideChatbot(),
                toggle: () => this.toggleChatbot(),
            };
            this.hideLauncher();
        },

        openChatbot() {
            if (!window.fcWidget) return;

            this.prepareWidgetForOpen();
            window.fcWidget.open();
        },

        prepareWidgetForOpen() {
            document.body.classList.add('chatbot-visible');

            const fcFrame = document.getElementById('fc_frame');
            if (fcFrame) {
                fcFrame.style.removeProperty('display');
                fcFrame.style.removeProperty('visibility');
                fcFrame.style.removeProperty('opacity');
            }
        },

        hideLauncher() {
            if (!this.shouldHideLauncher) return;

            document.querySelectorAll(FRESHCHAT_LAUNCHER_SELECTORS).forEach(element => {
                element.style.display = 'none';
                element.style.visibility = 'hidden';
                element.style.opacity = '0';
            });
        },

        hideWidgetOffPage() {
            document.body.classList.remove('chatbot-visible');

            const fcFrame = document.getElementById('fc_frame');
            if (fcFrame) {
                fcFrame.style.display = 'none';
                fcFrame.style.visibility = 'hidden';
                fcFrame.style.opacity = '0';
            }

            this.hideLauncher();
        },

        showChatbot() {
            if (!window.fcWidget) return;

            this.prepareWidgetForOpen();

            if (!this.shouldHideLauncher) {
                document.querySelectorAll(FRESHCHAT_LAUNCHER_SELECTORS).forEach(element => {
                    element.style.display = 'block';
                    element.style.visibility = 'visible';
                    element.style.opacity = '1';
                });
            }
        },

        hideChatbot() {
            if (!window.fcWidget) return;

            this.hideWidgetOffPage();
        },

        toggleChatbot() {
            if (!window.fcWidget) return;

            const fcFrame = document.getElementById('fc_frame');
            if (fcFrame && fcFrame.style.display !== 'none') {
                this.hideChatbot();
            } else {
                this.openChatbot();
            }
        },

        loadFreshchatScript() {
            return new Promise((resolve, reject) => {
                const script = document.createElement('script');
                const scriptUrl = this.chatbotConfig.scriptUrl.includes('?')
                    ? `${this.chatbotConfig.scriptUrl}&v=${Date.now()}`
                    : `${this.chatbotConfig.scriptUrl}?v=${Date.now()}`;
                script.id = FRESHCHAT_SCRIPT_ID;
                script.src = scriptUrl;
                script.async = true;
                // Freshworks requires chat="true" so the messenger iframe loads on
                // the Freshchat host. Without it, postMessage targets the Freshchat
                // origin while the frame is still on the app origin (e.g. staging).
                script.setAttribute('chat', 'true');
                script.setAttribute('widgetId', this.chatbotConfig.widgetId);
                script.onload = () => {
                    this.scriptLoaded = true;
                    resolve();
                };
                script.onerror = () => {
                    console.error('Chatbot: Failed to load Freshchat script.');
                    reject();
                };
                document.head.appendChild(script);
            });
        },

        destroyChatbot() {
            if (window.fcWidget && typeof window.fcWidget.destroy === 'function') {
                window.fcWidget.destroy();
            }

            this.setHelpPageActive(false);
            document.body.classList.remove('chatbot-visible');

            const fcFrame = document.getElementById('fc_frame');
            if (fcFrame) {
                fcFrame.remove();
            }

            delete window.edverumChatbot;
            delete window.fcWidget;
            this.isInitialized = false;
            this.scriptLoaded = false;
        },
    },
};
</script>
